import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { map } from 'rxjs/operators';
import { JoinResolverData } from 'src/app/pages/join/join.resolver';
import { AuthUser, OAuthCompany, SsoProviderType } from 'src/models';
import { ApiService } from '../api.service';
import { AuthService, CredentialAuthResponse, EditProfileProps, LoginResult, SsoOtpResponse, TokenResult, TokenScope } from './auth.service';

@Injectable({ providedIn: 'root' })
export class TokenAuthService extends AuthService {
  readonly version = 'v1';

  constructor(api: ApiService, store: Store) {
    super(api, store);
  }

  refreshToken(): Promise<unknown> {
    return Promise.reject({ error: 'invalidToken' });
  }

  login(email: string, password: string, totpCode?: string) {
    return this.api
      .request<{ data: LoginResult }>('post', `authorization/login`, {
        email,
        password,
        totpCode,
      }, { params: { company: '', 'no-workspaces': '1', 'has-managed-screencasts': '1' } })
      .pipe(map(x => x?.data))
      .toPromise();
  }

  logout() {
    return this.api.request('get', `logout`, {}, {}, true).toPromise();
  }

  auth() {
    return this.api
      .request<{ data: AuthUser }>('get', `authorization`, { 'no-workspaces': '1', company: '', 'has-managed-screencasts': true })
      .pipe(map(x => x?.data))
      .toPromise();
  }

  resetPassword(token: string, password: string) {
    return this.api.request('put', `profile`, { password }, { params: { company: '' }, headers: this.getTokenHeader(token) }).toPromise();
  }

  forgotPassword(email: string) {
    return this.api.request('get', `profile/restore-password`, { email }).toPromise();
  }

  async getScopedToken(scope: TokenScope): Promise<string> {
    return this.store.selectSnapshot(state => state.auth.token);
  }

  getTokenHeader(token: string): Record<string, string> {
    return { Authorization: `JWT ${token}` };
  }

  oauthLogin(provider: SsoProviderType, token: string) {
    return this.api.request<{ data: { token: string } }>
      ('post', `oauth2/${provider}/login`, {}, { params: { company: '' }, headers: this.getTokenHeader(token) })
      .pipe(map(x => x?.data))
      .toPromise();
  }

  findCompaniesByOTP(email: string, issuer: string, otp: string) {
    return this.api.request<{ data: OAuthCompany[] }>
      ('post', `oauth2/find-my-company/${otp}`, email ? { email } : { issuer }, { params: { company: '' } })
      .pipe(map(x => x?.data))
      .toPromise();
  }

  isSsoOtpEnabled(email: string) {
    return this.api.request<SsoOtpResponse>('post', `oauth2/check-sso-otp`, { email }).toPromise();
  }

  sendOtpRequest(email: string) {
    return this.api.request<{ data: {} }>('post', `oauth2/one-time-login`, { email }).toPromise();
  }

  register(email: string, name: string, password: string, params: any = null) {
    return this.api.request<{ data: TokenResult }>('post', 'register', { name, email, password, ...(params || {}) })
      .pipe(map(x => x?.data))
      .toPromise();
  }

  editProfile(properties: EditProfileProps) {
    return this.api.request<{ data: TokenResult }>('put', `profile`, properties).pipe(map(x => x.data)).toPromise();
  }

  validateMagicLinkToken(token: string): Promise<JoinResolverData> {
    return this.api.request<JoinResolverData>('post', 'register/validate-token', { token })
      .toPromise().catch(() => ({ valid: false }));
  }

  oauthGoogleLogin(token: string): Promise<CredentialAuthResponse> {
    const payload = this.parseJwt(token);
    const userEmail = payload['email'];
    if (!userEmail) return null;
    const body = {
      email: userEmail,
      timezone: Intl && Intl.DateTimeFormat().resolvedOptions().timeZone,
      splitTest: [
        { name: 'popup-signup-trigger', value: 'login-form' },
        { name: 'google-auth', value: true },
      ],
    };

    return this.api.request('post', 'oauth2/google/auth', body, { headers: this.getTokenHeader(token) }).toPromise();
  }
}
